import { isValid } from "date-fns";

export type ParsedRegEventDateTime = { when: Date | null; wasTimeProvided: boolean };

function normalizeTwoDigitYear(y: number) {
  if (y >= 0 && y <= 99) return 2000 + y;
  return y;
}

function parseUtcOffsetMinutes(input: string): { cleaned: string; offsetMinutes: number } {
  // Default: maintain current UX behavior when no timezone is provided
  // (Bulk modal help text says "assumes UTC-4")
  const DEFAULT_OFFSET_MINUTES = -4 * 60;

  // Accept "UTC-5", "UTC+1", "UTC-05", "UTC-5:30", "UTC-05:00"
  const re = /\s+UTC([+-])(\d{1,2})(?::(\d{2}))?\s*$/i;
  const m = re.exec(input);
  if (!m) return { cleaned: input.trim(), offsetMinutes: DEFAULT_OFFSET_MINUTES };

  const sign = m[1] === "+" ? 1 : -1;
  const hours = parseInt(m[2], 10);
  const minutes = m[3] ? parseInt(m[3], 10) : 0;
  const hoursOk = Number.isFinite(hours) && hours >= 0 && hours <= 23;
  const minutesOk = Number.isFinite(minutes) && minutes >= 0 && minutes <= 59;

  const offsetMinutes = hoursOk && minutesOk ? sign * (hours * 60 + minutes) : DEFAULT_OFFSET_MINUTES;
  const cleaned = input.slice(0, m.index).trim();
  return { cleaned, offsetMinutes };
}

export function parseRegEventDateTime(dateTimeStr: string): ParsedRegEventDateTime {
  if (!dateTimeStr || !dateTimeStr.trim()) {
    return { when: null, wasTimeProvided: false };
  }

  const { cleaned, offsetMinutes } = parseUtcOffsetMinutes(dateTimeStr.trim());

  // Try various datetime formats (with and without seconds)
  const formats = [
    // Month name with optional seconds
    /^(\w+)\s+(\d+),\s+(\d{4})\s+at\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s+(AM|PM)$/i,
    // Numeric date with optional 'at' word, optional seconds
    /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})\s+(?:at\s+)?(\d{1,2}):(\d{2})(?::(\d{2}))?\s+(AM|PM)$/i,
    // Date-only month name (no time)
    /^(\w+)\s+(\d+),\s+(\d{4})$/i,
    // Date-only numeric (no time)
    /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/i,
  ];

  for (const format of formats) {
    const match = cleaned.match(format);
    if (!match) continue;

    let month: number, day: number, year: number, hour: number, minute: number, second: number;
    let wasTimeProvided = false;

    // Determine if time was included by the presence of hour/minute groups
    const hasTime = typeof match[4] !== "undefined" && typeof match[5] !== "undefined";

    if (hasTime) {
      wasTimeProvided = true;

      // Decide whether the first capture is a month name or numeric month
      const firstToken = match[1];
      const isMonthName = isNaN(parseInt(firstToken as any, 10));
      if (isMonthName) {
        const monthName = match[1];
        const monthMap: Record<string, number> = {
          january: 0, jan: 0, february: 1, feb: 1, march: 2, mar: 2,
          april: 3, apr: 3, may: 4, june: 5, jun: 5, july: 6, jul: 6,
          august: 7, aug: 7, september: 8, sep: 8, sept: 8,
          october: 9, oct: 9, november: 10, nov: 10, december: 11, dec: 11,
        };
        month = monthMap[monthName.toLowerCase()] ?? 0;
        day = parseInt(match[2], 10);
        year = parseInt(match[3], 10);
        hour = parseInt(match[4], 10);
        minute = parseInt(match[5], 10);
        second = match[6] ? parseInt(match[6], 10) : 0;
        const ampm = match[7]?.toUpperCase();
        if (ampm === "PM" && hour !== 12) hour += 12;
        if (ampm === "AM" && hour === 12) hour = 0;
      } else {
        month = parseInt(match[1], 10) - 1;
        day = parseInt(match[2], 10);
        year = parseInt(match[3], 10);
        if (year < 100) year = normalizeTwoDigitYear(year);
        hour = parseInt(match[4], 10);
        minute = parseInt(match[5], 10);
        second = match[6] ? parseInt(match[6], 10) : 0;
        const ampm = match[7]?.toUpperCase();
        if (ampm === "PM" && hour !== 12) hour += 12;
        if (ampm === "AM" && hour === 12) hour = 0;
      }
    } else {
      wasTimeProvided = false;

      const firstToken2 = match[1];
      const isMonthName2 = isNaN(parseInt(firstToken2 as any, 10));
      if (isMonthName2) {
        const monthName = match[1];
        const monthMap: Record<string, number> = {
          january: 0, jan: 0, february: 1, feb: 1, march: 2, mar: 2,
          april: 3, apr: 3, may: 4, june: 5, jun: 5, july: 6, jul: 6,
          august: 7, aug: 7, september: 8, sep: 8, sept: 8,
          october: 9, oct: 9, november: 10, nov: 10, december: 11, dec: 11,
        };
        month = monthMap[monthName.toLowerCase()] ?? 0;
        day = parseInt(match[2], 10);
        year = parseInt(match[3], 10);
      } else {
        month = parseInt(match[1], 10) - 1;
        day = parseInt(match[2], 10);
        year = parseInt(match[3], 10);
        if (year < 100) year = normalizeTwoDigitYear(year);
      }
      hour = 0;
      minute = 0;
      second = 0;
    }

    // Interpret the input as "wall time in UTC±X", and convert to a UTC instant.
    // UTC = local - offset
    const utcMs = Date.UTC(year, month, day, hour ?? 0, minute ?? 0, second ?? 0) - offsetMinutes * 60 * 1000;
    const date = new Date(utcMs);
    if (isValid(date)) {
      return { when: date, wasTimeProvided };
    }
  }

  return { when: null, wasTimeProvided: false };
}


